import React, { useEffect, useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useHistory } from 'react-router-dom'
import { Formik, FormikProps } from 'formik'
import clsx from 'clsx'

import { pluralize } from '@pepper/utils/src/function/pluralize'
import {
  Button,
  usePaginatedProjects,
  useUtils,
  useProjectDetails,
  useGlobalState,
  useMessage,
} from '@pepper/ui'
import { createProject } from '@pepper/utils/src/api/projects/projects'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { TextField } from 'components/FormFields/FormFields'
import ModalWrapper, {
  ModalTitle,
  ModalBody,
  ModalFooter,
  showModalWrapper,
} from 'components/ModalWrapper'
import { createPrivateDocument } from 'utils/api/item'

import { generateDraftData } from './utils'
import VerticalSelector from './VerticalSelector'
import SuccessUI from './SuccessUI'
import { validationSchema } from './validationSchema'
import styles from './CreateContentModal.module.css'
import ProjectPicker from 'components/SelectProjectModal/ProjectPicker'

type TemplateStorageData = {
  templateId: string
  templateName: string
  templateCreatorName: string
  isTeamTemplate: boolean
}

type CreateContentModalModalProps = {
  topics: string[]
  contentType?: {
    value: number
  }
  shouldDirectToBrief?: boolean
  shouldCreatePrivateDoc?: boolean
  aiPromptContentType?: string
  modalType?: 'docs' | 'project'
  templateStorageData?: TemplateStorageData
}

export type FormValues = {
  projectId: string
  verticalId: number
  projectName?: string
}

type RecentContent = {
  type: string
  id: string
}

const initialValues: FormValues = {} as FormValues

const PAGE_SIZE = 10
const DEBOUNCE_DELAY = 800
const RECENT_CONTENT_LENGTH = 6

/**
 * @deprecated use `CreateContentModal` from `components/CreateItemModal/CreateItemModal.tsx`
 */
export const CreateContentModal =
  NiceModal.create<CreateContentModalModalProps>(
    ({
      topics,
      contentType,
      shouldDirectToBrief = false,
      shouldCreatePrivateDoc = false,
      aiPromptContentType,
      modalType = 'project',
      templateStorageData,
    }) => {
      const { visible: isModalVisible, remove: closeModal } = useModal()
      const history = useHistory()
      const companyId = useSelectedCompanyId()
      const { setPendingSidebarCountKeys } = useGlobalState()
      const { showError } = useMessage()
      const { verticals } = useUtils()

      const [isCreatingDraft, setIsCreatingDraft] = useState(false)
      const [isCreatingItem, setIsCreatingItem] = useState(false)
      const [draftId, setDraftId] = useState('')
      const [projectId, setProjectId] = useState('')

      const { projects, refetchProjects } = usePaginatedProjects({
        company_id: companyId,
        status: [
          'completed',
          'editing_progress',
          'fresh',
          'writing_progress',
          'closed',
        ],
        search_title: '',
        page_size: PAGE_SIZE,
        pagination_enabled: false,
      })

      const {
        data: projectDetails,
        isLoading: isLoadingProjectDetails,
        refetch: refetchProjectDetails,
      } = useProjectDetails(projectId)

      const handleCreateDoc = async (id: string) => {
        const { data } = await createPrivateDocument({
          projectId: id,
          templateId: templateStorageData?.templateId,
        })
        if (data?.status === 'success') {
          closeModal()
          let url = `/${companyId}/items/${data?.item_id}`
          if (aiPromptContentType) {
            url += `?content_type_id=${aiPromptContentType}`
          }
          window.open(url, '_blank')
        } else {
          showError('Unable to create document. Please try again')
        }
        setIsCreatingItem(false)
      }

      const handleSubmit = async (values: FormValues) => {
        try {
          setIsCreatingDraft(true)
          const {
            draftData,
            projectId: _projectId,
            error: _error = '',
          } = await generateDraftData({
            projectId: values.projectId,
            verticalId: values.verticalId,
            projectName: values.projectName,
            projectTitle: projectDetails?.project_title,
            topics: topics.map(topic => ({
              title: topic,
            })),
            companyId,
            verticals,
          })
          setPendingSidebarCountKeys(['allProjectsCount'])
          if (!draftData || !_projectId) {
            showError(_error || 'Unable to create Project')
            setIsCreatingDraft(false)
            return
          }
          refetchProjects()
          if (_projectId) {
            setProjectId(_projectId)
          }
          // Disable creating Draft in case of a document
          if (!shouldCreatePrivateDoc) {
            const {
              status,
              error,
              data: draftResponse,
            } = await createProject({
              body: draftData,
              type: 'draft',
            })
            setIsCreatingDraft(false)
            if (status !== 'ok') {
              showError(error)
            }
            refetchProjectDetails()
            setDraftId(draftResponse.id)
          }

          if (shouldCreatePrivateDoc) {
            setIsCreatingItem(true)
            handleCreateDoc(_projectId)
            const recentItems = localStorage.getItem('RECENT_CONTENT')
            if (!recentItems) {
              const recentItemsList = []
              if (templateStorageData) {
                recentItemsList[0] = {
                  type: 'template',
                  id: templateStorageData.templateId,
                  templateName: templateStorageData.templateName,
                  templateCreatorName: templateStorageData.templateCreatorName,
                  isTeamTemplate: templateStorageData.isTeamTemplate,
                }
              } else {
                recentItemsList[0] = {
                  type: 'content',
                  id: aiPromptContentType,
                }
              }
              localStorage.setItem(
                'RECENT_CONTENT',
                JSON.stringify(recentItemsList)
              )
            } else {
              let recentItemsList = JSON.parse(recentItems)
              if (templateStorageData) {
                recentItemsList.unshift({
                  type: 'template',
                  id: templateStorageData.templateId,
                  templateName: templateStorageData.templateName,
                  templateCreatorName: templateStorageData.templateCreatorName,
                  isTeamTemplate: templateStorageData.isTeamTemplate,
                })
              } else {
                recentItemsList.unshift({
                  type: 'content',
                  id: aiPromptContentType,
                })
              }
              recentItemsList = recentItemsList.filter(
                (
                  value: RecentContent,
                  index: number,
                  self: RecentContent[]
                ) => {
                  return (
                    index ===
                    self.findIndex(
                      (item: RecentContent) => item.id === value.id
                    )
                  )
                }
              )
              if (recentItemsList.length > RECENT_CONTENT_LENGTH) {
                recentItemsList.pop()
              }
              localStorage.setItem(
                'RECENT_CONTENT',
                JSON.stringify(recentItemsList)
              )
            }
          }
        } catch (error) {
          showError(error)
          setIsCreatingDraft(false)
        }
      }

      useEffect(() => {
        if (isModalVisible) {
          showModalWrapper()
        }
      }, [isModalVisible])

      const selectedProject = projects.find(project => project.id === projectId)

      const getDefaultValue = (props: FormikProps<FormValues>) => {
        props?.setFieldValue('verticalId', [contentType?.value])
        return contentType?.value
      }

      if (
        !shouldCreatePrivateDoc &&
        shouldDirectToBrief &&
        draftId &&
        projectId
      ) {
        history.push({
          pathname: '/create-project-flow',
          search: `?draft_id=${draftId}&projectId=${projectId}`,
        })
        closeModal()
      }

      return (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {(props): React.ReactNode => {
            const hasSelectedExistingProject =
              !!props.values?.projectId && props.values?.projectId !== 'new'
            const showCreateProject = props.values.projectId === 'new'

            return (
              <ModalWrapper id="seo-headless-modal" onCancel={closeModal}>
                <ModalTitle>Create Content</ModalTitle>
                {draftId &&
                selectedProject &&
                (!shouldCreatePrivateDoc || modalType === 'docs') ? (
                  <SuccessUI
                    title={
                      props.values?.projectName || selectedProject.project_title
                    }
                    topics={topics}
                    topicsCount={topics.length}
                    itemsCount={projectDetails?.items_draft_count}
                    projectId={projectId}
                    draftId={draftId}
                    closeModal={closeModal}
                    modalType={modalType}
                    companyId={companyId}
                    shouldCreatePrivateDoc={shouldCreatePrivateDoc}
                  />
                ) : (
                  <>
                    <ModalBody>
                      <div className={styles.contentModalBody}>
                        <div
                          className={clsx(
                            styles.selectField,
                            styles.inputV2Wrap
                          )}
                          data-cy="projectId"
                        >
                          {!showCreateProject && (
                            <ProjectPicker
                              onProjectClick={id => {
                                setProjectId(id)
                                props.setFieldValue('projectId', id)
                              }}
                              onCreateNew={() => {
                                props.setFieldValue('projectId', 'new')
                              }}
                            />
                          )}
                        </div>
                        {showCreateProject && (
                          <TextField
                            name="projectName"
                            label="Project Name"
                            required={true}
                            fieldProps={{
                              placeholder: 'Enter the new project name',
                              loadV2: true,
                            }}
                          />
                        )}
                        {!shouldCreatePrivateDoc && (
                          <VerticalSelector
                            formikProps={props}
                            defaultValue={getDefaultValue}
                          />
                        )}
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      {!shouldDirectToBrief && !shouldCreatePrivateDoc && (
                        <span className={styles.contentModalFooterInfo}>
                          Creating order for {pluralize(topics.length, 'topic')}
                        </span>
                      )}
                      <Button
                        type="link"
                        onClick={closeModal}
                        className={styles.contentModalFooterButton}
                        disabled={isCreatingDraft}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={props.submitForm}
                        disabled={!props.dirty || !props.isValid}
                        loading={
                          isCreatingDraft ||
                          isCreatingItem ||
                          (hasSelectedExistingProject &&
                            isLoadingProjectDetails)
                        }
                        className={clsx(
                          styles.contentModalFooterButton,
                          styles.submitButton
                        )}
                      >
                        {shouldCreatePrivateDoc ? 'Create' : 'Next'}
                      </Button>
                    </ModalFooter>
                  </>
                )}
              </ModalWrapper>
            )
          }}
        </Formik>
      )
    }
  )
